import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { CurrencyDollar, TrendUp, Package, Users } from "@phosphor-icons/react";
import { toast } from "sonner";

const WINDOWS = [
  { key: "30d", label_key: "revenue.win_30d" },
  { key: "90d", label_key: "revenue.win_90d" },
  { key: "365d", label_key: "revenue.win_365d" },
];

function fmtMoney(n) {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

export default function RevenueWidget({ refreshTick = 0 }) {
  const { t } = useTranslation();
  const [windowKey, setWindowKey] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api
        .get(`/revenue?window=${windowKey}`)
        .then(({ data: d }) => {
          if (cancelled) return;
          setData(d);
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          toast.error(formatApiError(e));
          setLoading(false);
        });
    load();
    // v414.56 — refetch when the tab regains focus so the total is never
    // stale after marking orders paid on another page/tab (owner report:
    // "marked an ABL order paid but revenue did not seem to update").
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [windowKey, refreshTick]);

  return (
    <section data-testid="revenue-widget" className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-zinc-200 bg-gradient-to-r from-zinc-50 to-white">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <CurrencyDollar size={22} weight="bold" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold tracking-tight">{t("revenue.title")}</h2>
            <p className="text-xs text-zinc-500">{t("revenue.subtitle")}</p>
          </div>
        </div>
        <div className="inline-flex border border-zinc-200 rounded-lg overflow-hidden" data-testid="revenue-window-toggle">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindowKey(w.key)}
              data-testid={`revenue-window-${w.key}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                windowKey === w.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
              }`}
            >
              {t(w.label_key)}
            </button>
          ))}
        </div>
      </header>

      {!loading && data?.sources && (
        <SourceBreakdown t={t} sources={data.sources} />
      )}

      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-zinc-100">
        <KPI
          testId="revenue-total"
          icon={CurrencyDollar}
          label={t("revenue.total_revenue")}
          value={loading ? "…" : fmtMoney(data?.total_revenue)}
          tone="emerald"
          to="/sales-history"
        />
        <KPI
          testId="revenue-units"
          icon={TrendUp}
          label={t("revenue.units_sold")}
          value={loading ? "…" : (data?.total_units_sold ?? 0).toLocaleString()}
          tone="gold"
          to="/sales-history"
        />
        <KPI
          testId="revenue-sale-count"
          icon={Package}
          label={t("revenue.sale_count")}
          value={loading ? "…" : (data?.sale_count ?? 0).toLocaleString()}
          tone="zinc"
          to="/sales-history"
        />
      </div>

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <RankList
          testId="revenue-top-distributors"
          icon={Users}
          title={t("revenue.top_distributors")}
          empty={t("revenue.empty_distributors")}
          loading={loading}
          rows={data?.top_distributors || []}
          rowKey={(r) => r.id || r.email || r.name}
          renderRow={(r) => ({
            primary: r.name || t("revenue.unknown"),
            secondary: r.email || "",
            amount: fmtMoney(r.revenue),
            units: t("revenue.units_n", { n: r.units }),
          })}
        />
        <RankList
          testId="revenue-top-products"
          icon={Package}
          title={t("revenue.top_products")}
          empty={t("revenue.empty_products")}
          loading={loading}
          rows={data?.top_products || []}
          rowKey={(r) => r.id || r.name}
          renderRow={(r) => ({
            primary: r.name,
            secondary: "",
            amount: fmtMoney(r.revenue),
            units: t("revenue.units_n", { n: r.units }),
          })}
        />
      </div>
    </section>
  );
}

// v414.60 — per-source slices so the owner can SEE that store orders and
// Active Bio Labs money landed in the total (recurring report: "marked an
// ABL order paid but revenue did not update").
function SourceBreakdown({ t, sources }) {
  const slices = [
    { key: "inventory", label: t("revenue.src_inventory"), ...sources.inventory },
    { key: "store_orders", label: t("revenue.src_store"), ...sources.store_orders },
    { key: "abl", label: t("revenue.src_abl"), ...sources.abl },
  ].filter((s) => (s.count || 0) > 0 || (s.revenue || 0) > 0);
  if (slices.length === 0) return null;
  return (
    <div
      data-testid="revenue-source-breakdown"
      className="px-5 py-3 border-b border-zinc-100 bg-zinc-50/60 flex flex-wrap items-center gap-x-5 gap-y-1"
    >
      <span className="text-[11px] uppercase font-semibold tracking-wide text-zinc-400">
        {t("revenue.src_title")}
      </span>
      {slices.map((s) => (
        <span key={s.key} data-testid={`revenue-src-${s.key}`} className="text-xs text-zinc-600">
          {s.label}{" "}
          <span className="font-semibold text-emerald-700 tabular-nums">{fmtMoney(s.revenue)}</span>
          <span className="text-zinc-400"> · {t("revenue.src_n_sales", { n: s.count })}</span>
        </span>
      ))}
    </div>
  );
}

function KPI({ testId, icon: Icon, label, value, tone, to }) {
  const toneClass = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    gold: "bg-[#fbf6e7] text-[#8a6a1c] border-[#c9a24a]",
    zinc: "bg-zinc-50 text-zinc-700 border-zinc-200",
  }[tone] || "bg-zinc-50 text-zinc-700 border-zinc-200";
  const body = (
    <>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} weight="bold" />
        <span className="text-[11px] uppercase font-semibold tracking-wide opacity-70">{label}</span>
      </div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </>
  );
  const base = `rounded-lg border p-4 ${toneClass}`;
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={`${base} block hover:brightness-95 transition`}>
        {body}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className={base}>
      {body}
    </div>
  );
}

function RankList({ testId, icon: Icon, title, empty, loading, rows, rowKey, renderRow }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="bold" className="text-zinc-500" />
        <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
      </div>
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 bg-zinc-50 border border-zinc-100 rounded-md animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-md p-3">{empty}</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => {
            const v = renderRow(r);
            return (
              <li
                key={rowKey(r)}
                data-testid={`${testId}-row-${i}`}
                className="flex items-center justify-between gap-3 py-2 px-3 border border-zinc-200 rounded-md bg-white hover:bg-zinc-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-semibold text-zinc-400 w-5 text-right shrink-0">{i + 1}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">{v.primary}</p>
                    {v.secondary && <p className="text-[11px] text-zinc-500 truncate">{v.secondary}</p>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold text-emerald-700 tabular-nums">{v.amount}</p>
                  <p className="text-[11px] text-zinc-500">{v.units}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
